import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { DocumentType, FileData } from '@applyboard/school-applications-types-lib'
import { UploadFileData } from '../types'

interface IApplicationFormContext {
  addPendingUpload: (fileId: string, uploadFileData: UploadFileData) => void
  addPendingDelete: (fileId: string) => void
  getObservableFiles: (filters: {
    fileType?: string | string[]
    sectionReference?: string
  }) => Record<string, Omit<UploadFileData, 'file'>>
  pendingFileUploadState: Record<string, UploadFileData | null>
  pendingUpload: Record<string, UploadFileData>
  resetFiles: (fileData: FileData) => void
  uploadedFiles: FileData
  visibleFileState: Record<string, Omit<UploadFileData, 'file'>>
}

const DOCUMENT_PROCESSING_FEATURE_TYPES = ['PASSPORT', 
  'LANGUAGE_TEST_IELTS', 
  'LANGUAGE_TEST_PTE', 
  'LANGUAGE_TEST_TOEFL', 
  'LANGUAGE_TEST_OTHER', 
  'LANGUAGE_TEST_DUOLINGO', 
  'LANGUAGE_TEST_CAEL', 
  'LANGUAGE_TEST_CAMBRIDGE', 
  'LANGUAGE_TEST_MET', 
  'LANGUAGE_TEST_EPP', 
  'LANGUAGE_TEST', 
  'LANGUAGE_TEST_CANTEST', 
  'LANGUAGE_TEST_LANGUAGECERT_ESOL', 
  'LANGUAGE_TEST_LEAP_4_ADVANCED',
];

const ApplicationFormContext = createContext<IApplicationFormContext | undefined>(undefined)

export function useApplicationFormContext(): IApplicationFormContext {
  return useContext(ApplicationFormContext) as IApplicationFormContext
}

export function ApplicationForm({
  children,
  fileData,
  documentProcessingFeatureEnabled = false,
}: Readonly<{
  children: React.ReactNode
  fileData?: FileData,
  documentProcessingFeatureEnabled?: boolean
}>) {
  const [pendingDelete, setPendingDelete] = useState<string[]>([])
  const [pendingUpload, setPendingUpload] = useState<Record<string, UploadFileData>>({})
  const [uploadedFiles, setUploadedFiles] = useState<FileData>(fileData || {})

  const resetFiles = useCallback((fileData: FileData) => {
    setPendingDelete([])
    setPendingUpload({})
    setUploadedFiles(fileData || {})
  }, [])

  // file state to display
  const visibleFileState: Record<string, Omit<UploadFileData, 'file'>> = useMemo(() => {
    const visibleUploadedFiles = Object.keys(uploadedFiles).reduce(
      (acc, id) => {
        if (!uploadedFiles[id]) return acc

        acc[id] = {
          contentType: uploadedFiles[id]?.contentType as string,
          fileName: uploadedFiles[id]?.fileName as string,
          hash: uploadedFiles[id]?.hash as string,
          type: uploadedFiles[id]?.type as DocumentType,
          sectionReference: uploadedFiles[id]?.sectionReference as string,
        }
        return acc
      },
      {} as Record<string, Omit<UploadFileData, 'file'>>,
    )

    const result = {
      ...visibleUploadedFiles,
      ...pendingUpload,
    }

    pendingDelete.forEach(id => {
      if (result[id]) {
        delete result[id]
      }
    })

    return result
  }, [pendingDelete, pendingUpload, uploadedFiles])

  // upload state to submit, only includes pending changes
  const pendingFileUploadState: Record<string, UploadFileData | null> = useMemo(() => {
    return {
      ...pendingUpload,
      ...pendingDelete.reduce(
        (acc, fileId) => {
          acc[fileId] = null
          return acc
        },
        {} as Record<string, null>,
      ),
    }
  }, [pendingUpload, pendingDelete])

  function addPendingUpload(fileId: string, uploadFileData: UploadFileData) {
    if (uploadedFiles[fileId] || (documentProcessingFeatureEnabled && DOCUMENT_PROCESSING_FEATURE_TYPES.includes(uploadFileData.type))) return

    setPendingUpload(pendingUpload => ({
      ...pendingUpload,
      [fileId]: uploadFileData,
    }))
  }

  function addPendingDelete(fileId: string) {
    if (pendingUpload[fileId]) {
      setPendingUpload(pendingUpload => {
        const { [fileId]: _, ...rest } = pendingUpload
        return rest
      })
    } else if (uploadedFiles[fileId]) {
      setPendingDelete(pendingDelete => {
        return [...pendingDelete, fileId]
      })
    }
  }

  // visible files with filtering
  function getObservableFiles(filters: {
    fileType?: string | string[]
    sectionReference?: string
  }): Record<string, Omit<UploadFileData, 'file'>> {
    return Object.entries(visibleFileState).reduce(
      (observableFiles, [fileId, fileData]) => {
        if (
          (Array.isArray(filters.fileType) && !filters.fileType.includes(fileData.type)) ||
          (!Array.isArray(filters.fileType) && filters.fileType && filters.fileType !== fileData.type) ||
          (filters.sectionReference && filters.sectionReference !== fileData.sectionReference)
        )
          return observableFiles

        return { ...observableFiles, [fileId]: fileData }
      },
      {} as Record<string, Omit<UploadFileData, 'file'>>,
    )
  }

  return (
    <ApplicationFormContext.Provider
      value={{
        addPendingDelete,
        addPendingUpload,
        getObservableFiles,
        pendingFileUploadState,
        pendingUpload,
        resetFiles,
        uploadedFiles,
        visibleFileState,
      }}
    >
      {children}
    </ApplicationFormContext.Provider>
  )
}
